/**
 * SignaturePad — pointer-driven canvas the enrolment wizard uses to capture
 * a drawn IRD Authority-to-Act signature. Exposes `toDataURL()` and
 * `isEmpty()` on the forwarded ref so the parent can grab the PNG when the
 * client submits and validate that they actually drew something.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const SignaturePad = forwardRef(function SignaturePad({ height = 160, testId = "signature-pad" }, ref) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const lastPoint = useRef(null);
    const [hasStrokes, setHasStrokes] = useState(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Set the canvas backing store to the CSS size × devicePixelRatio so
        // strokes stay crisp on retina + phones.
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.lineWidth = 2.2;
        ctx.strokeStyle = "#0F1115";
    }, []);

    useImperativeHandle(ref, () => ({
        clear() {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const ctx = canvas.getContext("2d");
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
            setHasStrokes(false);
        },
        isEmpty() { return !hasStrokes; },
        toDataURL() {
            const canvas = canvasRef.current;
            if (!canvas) return null;
            return canvas.toDataURL("image/png");
        },
    }));

    function xy(e) {
        const rect = canvasRef.current.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    }

    function start(e) {
        e.preventDefault();
        drawing.current = true;
        lastPoint.current = xy(e);
    }
    function move(e) {
        if (!drawing.current) return;
        e.preventDefault();
        const p = xy(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastPoint.current = p;
        if (!hasStrokes) setHasStrokes(true);
    }
    function end() { drawing.current = false; }

    return (
        <div className="relative">
            <canvas
                ref={canvasRef}
                data-testid={testId}
                style={{ width: "100%", height: `${height}px`, touchAction: "none" }}
                className="w-full rounded-md border border-dashed border-[#D4D4D8] bg-white cursor-crosshair"
                onPointerDown={start}
                onPointerMove={move}
                onPointerUp={end}
                onPointerLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
            />
            {!hasStrokes && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[13px] text-[#B4B4BC]">
                    Sign here with mouse or finger
                </div>
            )}
        </div>
    );
});

export default SignaturePad;
